import { onAuthStateChanged, signOut } from 'firebase/auth';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { auth } from './firebase/client';
import { AppDialogHost } from './components/AppDialogHost';
import { ToastHost } from './components/ToastHost';
import { MembershipAccessGate } from './components/MembershipAccessGate';
import { LoginPage } from './pages/LoginPage';
import { WorkspaceSetupPage } from './pages/WorkspaceSetupPage';
import { QuickGuidePage } from './pages/QuickGuidePage';
import { getCompaniesForUser } from './services/companyService';

const PhirtiShell = lazy(() =>
  import('./pages/PhirtiShell').then((m) => ({ default: m.PhirtiShell })),
);

export default function App() {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(() => auth().currentUser);
  const [hasWorkspaces, setHasWorkspaces] = useState<boolean | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth(), (u) => {
      setUser(u);
      if (!u?.uid) {
        setHasWorkspaces(null);
        setReady(true);
        return;
      }
      const phone = u.phoneNumber?.trim();
      if (!phone) {
        void signOut(auth());
        setHasWorkspaces(null);
        setReady(true);
        return;
      }
      setReady(false);
      getCompaniesForUser(u.uid)
        .then((list) => {
          setHasWorkspaces(list.length > 0);
          setReady(true);
        })
        .catch(() => {
          setHasWorkspaces(false);
          setReady(true);
        });
    });
  }, []);

  if (!ready) {
    return (
      <div className="app-loading-screen">
        <span style={{ color: 'var(--ph-muted)' }}>Loading…</span>
      </div>
    );
  }

  return (
    <div className="app-routes-fill">
      <AppDialogHost />
      <ToastHost />
      <Routes>
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to={hasWorkspaces ? '/' : '/workspace'} replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route
          path="/workspace"
          element={
            !user ? (
              <Navigate to="/login" replace />
            ) : hasWorkspaces ? (
              <Navigate to="/" replace />
            ) : (
              <WorkspaceSetupPage />
            )
          }
        />
        <Route
          path="/guide"
          element={
            !user ? (
              <Navigate to="/login" replace />
            ) : (
              <QuickGuidePage />
            )
          }
        />
        <Route
          path="/*"
          element={
            !user ? (
              <Navigate to="/login" replace />
            ) : !hasWorkspaces ? (
              <Navigate to="/workspace" replace />
            ) : (
              <Suspense
                fallback={
                  <div className="app-loading-screen">
                    <span style={{ color: 'var(--ph-muted)' }}>Loading…</span>
                  </div>
                }
              >
                <MembershipAccessGate uid={user.uid}>
                  <PhirtiShell />
                </MembershipAccessGate>
              </Suspense>
            )
          }
        />
      </Routes>
    </div>
  );
}
